import { z } from 'zod';
import { BrowserDialogSchema, CursorSchema } from './browser-tools.js';
import { BrowserRuntimeSchema, ErrorInfoSchema, IdSchema, InputEventSchema, ParticipantKindSchema, ParticipantSchema, SupportSessionStatusSchema, TabInfoSchema, WindowSizeSchema, } from './common.js';
import { StreamModeSchema, StreamParamsSchema } from './modes.js';
import { ReplySchema } from './node.js';
// Viewer channel: browser <-> server over /ws/viewer. One socket is one participant in one profile.
const Req = z.object({ id: IdSchema });
export const ViewerToServerSchema = z.discriminatedUnion('t', [
    Req.extend({ t: z.literal('clipboard.read'), tabId: IdSchema }),
    Req.extend({
        t: z.literal('dialog.respond'),
        tabId: IdSchema,
        dialogId: IdSchema,
        accept: z.boolean(),
        promptText: z.string().max(20000).optional(),
    }),
    Req.extend({
        t: z.literal('join'),
        protocolVersion: z.number().int(),
        profileId: IdSchema,
        /** Operators may associate their view with a specific support session. */
        supportSessionId: IdSchema.optional(),
        /** Preferred tab; defaults to the profile's active tab */
        tabId: IdSchema.optional(),
        stream: StreamParamsSchema,
    }),
    z.object({ t: z.literal('leave') }),
    Req.extend({
        t: z.literal('tab.select'),
        tabId: IdSchema,
        stream: StreamParamsSchema.optional(),
    }),
    Req.extend({ t: z.literal('tab.create'), url: z.string() }),
    Req.extend({ t: z.literal('tab.close'), tabId: IdSchema }),
    Req.extend({ t: z.literal('tab.navigate'), tabId: IdSchema, url: z.string() }),
    Req.extend({ t: z.literal('tab.back'), tabId: IdSchema }),
    Req.extend({ t: z.literal('tab.forward'), tabId: IdSchema }),
    Req.extend({ t: z.literal('tab.reload'), tabId: IdSchema }),
    Req.extend({ t: z.literal('control.request') }),
    Req.extend({ t: z.literal('control.release') }),
    Req.extend({ t: z.literal('control.grant'), participantId: IdSchema }),
    Req.extend({ t: z.literal('control.deny'), participantId: IdSchema }),
    /** admin only: take control regardless of current holder */
    Req.extend({ t: z.literal('control.take') }),
    Req.extend({ t: z.literal('window.resize'), size: WindowSizeSchema }),
    Req.extend({ t: z.literal('browser.open') }),
    Req.extend({ t: z.literal('browser.close') }),
    Req.extend({ t: z.literal('customer.done') }),
    Req.extend({
        t: z.literal('file.begin'),
        tabId: IdSchema,
        chooserId: IdSchema,
        files: z
            .array(z.object({
            name: z.string().min(1).max(255),
            size: z.number().int().min(0),
            mime: z.string().max(128),
        }))
            .min(1)
            .max(20),
    }),
    Req.extend({ t: z.literal('file.end'), tabId: IdSchema, chooserId: IdSchema }),
    z.object({ t: z.literal('file.cancel'), tabId: IdSchema, chooserId: IdSchema }),
    z.object({ t: z.literal('input'), event: InputEventSchema }),
    z.object({ t: z.literal('ping'), ts: z.number() }),
]);
/** Who holds control. Control is reserved across disconnects, so `connected` may be false. */
export const ControllerInfoSchema = z.object({
    participantId: IdSchema,
    name: z.string(),
    kind: ParticipantKindSchema,
    connected: z.boolean(),
});
export const ViewerProfileSnapshotSchema = z.object({
    dialogs: z.array(BrowserDialogSchema).optional(),
    browserTools: z.boolean().optional(),
    profileId: IdSchema,
    name: z.string(),
    runtime: BrowserRuntimeSchema,
    supportSession: z.object({ id: IdSchema, status: SupportSessionStatusSchema }).optional(),
    size: WindowSizeSchema,
    activeTabId: IdSchema.optional(),
    tabs: z.array(TabInfoSchema),
    participants: z.array(ParticipantSchema),
    controllerId: IdSchema.optional(),
    controller: ControllerInfoSchema.optional(),
    /** modes the node advertises intersected with modes the server knows */
    availableModes: z.array(StreamModeSchema),
    nodeOnline: z.boolean(),
});
export const ServerToViewerSchema = z.union([
    ReplySchema,
    z.discriminatedUnion('t', [
        z.object({ t: z.literal('dialog.opened'), dialog: BrowserDialogSchema }),
        z.object({ t: z.literal('dialog.closed'), tabId: IdSchema, dialogId: IdSchema }),
        z.object({ t: z.literal('cursor.changed'), tabId: IdSchema, cursor: CursorSchema }),
        z.object({
            t: z.literal('joined'),
            participantId: IdSchema,
            kind: ParticipantKindSchema,
            can: z.object({
                navigate: z.boolean(),
                createTab: z.boolean(),
                closeTab: z.boolean(),
                resize: z.boolean(),
                openBrowser: z.boolean(),
                forceControl: z.boolean(),
            }),
            snapshot: ViewerProfileSnapshotSchema,
            mode: StreamModeSchema,
            tabId: IdSchema.optional(),
        }),
        z.object({ t: z.literal('snapshot'), snapshot: ViewerProfileSnapshotSchema }),
        z.object({
            t: z.literal('presence'),
            participants: z.array(ParticipantSchema),
            controllerId: IdSchema.optional(),
            controller: ControllerInfoSchema.optional(),
        }),
        z.object({
            t: z.literal('tabs.changed'),
            tabs: z.array(TabInfoSchema),
            activeTabId: IdSchema.optional(),
        }),
        z.object({
            t: z.literal('browser.state'),
            runtime: BrowserRuntimeSchema,
            error: ErrorInfoSchema.optional(),
        }),
        z.object({
            t: z.literal('support-session.state'),
            supportSessionId: IdSchema,
            status: SupportSessionStatusSchema,
        }),
        z.object({
            t: z.literal('tab.navigated'),
            tabId: IdSchema,
            url: z.string(),
            title: z.string().optional(),
            blocked: z.boolean().default(false),
        }),
        z.object({
            t: z.literal('stream.state'),
            tabId: IdSchema,
            state: z.enum(['active', 'paused', 'stopped']),
            reason: z
                .enum(['inactive_tab', 'minimized', 'no_viewers', 'error', 'node_offline'])
                .optional(),
        }),
        z.object({
            t: z.literal('control.changed'),
            controllerId: IdSchema.optional(),
            reason: z.enum(['granted', 'released', 'taken', 'auto', 'left', 'timeout']),
        }),
        z.object({
            t: z.literal('control.requested'),
            participantId: IdSchema,
            name: z.string(),
            kind: ParticipantKindSchema,
        }),
        z.object({ t: z.literal('control.denied'), by: IdSchema }),
        z.object({ t: z.literal('window.resized'), size: WindowSizeSchema }),
        z.object({
            t: z.literal('filechooser.opened'),
            tabId: IdSchema,
            chooserId: IdSchema,
            multiple: z.boolean(),
        }),
        z.object({ t: z.literal('filechooser.closed'), tabId: IdSchema, chooserId: IdSchema }),
        z.object({ t: z.literal('file.progress'), chooserId: IdSchema, receivedBytes: z.number() }),
        z.object({
            t: z.literal('notice'),
            level: z.enum(['info', 'warn', 'error']),
            message: z.string(),
        }),
        z.object({ t: z.literal('kicked'), reason: z.string() }),
        z.object({ t: z.literal('pong'), ts: z.number() }),
    ]),
]);
//# sourceMappingURL=viewer.js.map